import React, { useEffect, useState, useMemo } from "react";
import "../../Styles/invoiceTable.css";
import { toast } from "react-toastify";
import { BsSearch } from "react-icons/bs";
import html2pdf from "html2pdf.js";
import TablePagination from "@mui/material/TablePagination";
import ReLoginModal from "../../components/ReLoginModal";
import * as XLSX from "xlsx";
import { CircularProgress } from "@mui/material";
import api from "../../util/axios";

type Allocation = {
  license: {
    licenseId: string;
    licenseNumber: string;
    licenseOwner: string;
  };
  destination: string;
  invoice?: string;
  truckNumber: string;
  itemNumber: string;
  product: { id: string; name: string };
  customer: { id: string; businessName: string };
  exDepotPrice: number;
  quantity: number;
  _id: string;
  updatedAt: string;
  createdAt: string;
  status: string;
};

const AllocationReport = () => {
  const [getAllocations, setGetAllocations] = useState<Allocation[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [reLogin, setReLogin] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [businessQuery, setBusinessQuery] = useState("");
  const [truckQuery, setTruckQuery] = useState("");
  const [searchField, setSearchField] = useState("businessName");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = window.localStorage.getItem("token");

  const queryAllocations = () => {
    setIsLoading(true);
    setError(null);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const queryParams =
      fromDate || toDate
        ? `?startDate=${fromDate || ""}&endDate=${toDate || ""}`
        : "";

    api
      .get(`/daily-allocations${queryParams}`, { headers })
      .then((res) => {
        setGetAllocations(res.data.data.dailyAllocations);
      })
      .catch((error) => {
        if (
          error.response?.data?.message === "jwt expired" ||
          error.response?.data?.message === "jwt malformed"
        ) {
          setReLogin(true);
        } else {
          setError("Failed to fetch allocations. Please try again later.");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    queryAllocations();
  }, [fromDate, toDate]);

  const filteredAllocations = useMemo(() => {
    return getAllocations.filter((allocation) => {
      const allocationDate = new Date(allocation.createdAt);
      const startDate = fromDate ? new Date(fromDate) : null;
      const endDate = toDate ? new Date(toDate) : null;

      // Date filtering
      if (startDate && allocationDate < startDate) return false;
      if (endDate && allocationDate > endDate) return false;

      // Status filtering
      if (statusFilter !== "all" && allocation.status.toLowerCase() !== statusFilter.toLowerCase()) {
        return false;
      }

      // Search filtering
      if (searchField === "combined") {
        if (!businessQuery && !productQuery && !truckQuery) return true;
      } else if (!searchQuery) {
        return true;
      }

      const customerName = allocation.customer?.businessName?.toLowerCase() || "";
      const productName = allocation.product?.name?.toLowerCase() || allocation.itemNumber?.toLowerCase() || "";
      const truckNumber = allocation.truckNumber?.toLowerCase() || "";

      const businessNameMatch = customerName.includes(
        searchField === "combined" ? businessQuery.toLowerCase() : searchQuery.toLowerCase()
      );
      const productNameMatch = productName.includes(
        searchField === "combined" ? productQuery.toLowerCase() : searchQuery.toLowerCase()
      );
      const truckNumberMatch = truckNumber.includes(
        searchField === "combined" ? truckQuery.toLowerCase() : searchQuery.toLowerCase()
      );

      switch (searchField) {
        case "businessName":
          return businessNameMatch;
        case "productName":
          return productNameMatch;
        case "truckNumber":
          return truckNumberMatch;
        case "combined":
          return businessNameMatch && productNameMatch && truckNumberMatch;
        default:
          return businessNameMatch;
      }
    });
  }, [getAllocations, fromDate, toDate, searchQuery, productQuery, businessQuery, truckQuery, searchField, statusFilter]);

  const paginatedAllocations = filteredAllocations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const generatePDF = () => {
    const slipElement = document.querySelector(".invoiceTable");
    const dowBtnElement = document.querySelector(".dowBtn");

    if (slipElement) {
      if (dowBtnElement) (dowBtnElement as HTMLElement).style.display = "none";

      const pdfOptions = {
        margin: 2,
        filename: "Allocation-Report.pdf",
        image: { type: "png" },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      };
      html2pdf()
        .from(slipElement)
        .set(pdfOptions)
        .save()
        .finally(() => {
          if (dowBtnElement) (dowBtnElement as HTMLElement).style.display = "block";
        });
    }
  };

  const exportToExcelExternal = () => {
    if (!filteredAllocations || filteredAllocations.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    const filteredData = filteredAllocations.map((allocation, index) => ({
      "S/N": index + 1,
      "Truck No.": allocation.truckNumber || "N/A",
      "Quantity": allocation.quantity || 0,
      "License Owner": allocation.license?.licenseOwner || "N/A",
      "License No": allocation.license?.licenseNumber || "N/A",
      "Destination": allocation.destination || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = [
      { width: 5 },
      { width: 15 },
      { width: 15 },
      { width: 30 },
      { width: 25 },
      { width: 50 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AllocationReports");
    XLSX.writeFile(wb, "Allocation-Report-ext.xlsx");
  };

  const exportToExcel = () => {
    if (!filteredAllocations || filteredAllocations.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    const filteredData = filteredAllocations.map((allocation, index) => ({
      "S/N": index + 1,
      "License No": allocation.license?.licenseNumber || "N/A",
      "Customer": allocation.customer?.businessName || "N/A",
      "License Owner": allocation.license?.licenseOwner || "N/A",
      "Destination": allocation.destination || "N/A",
      "Waybill": allocation.invoice || "N/A",
      "Truck No": allocation.truckNumber || "N/A",
      "Product": allocation.product?.name || allocation.itemNumber || "N/A",
      "Quantity": allocation.quantity || 0,
      "Price": allocation.exDepotPrice || 0,
      "Status": allocation.status || "N/A",
      "Date": new Date(allocation.updatedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      }),
    }));

    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = [
      { width: 5 },
      { width: 15 },
      { width: 25 },
      { width: 30 },
      { width: 50 },
      { width: 25 },
      { width: 15 },
      { width: 20 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AllocationReports");
    XLSX.writeFile(wb, "Allocation-Report.xlsx");
  };

  return (
    <div className="board">
      {reLogin && <ReLoginModal />}
      <div className="method">
        <div className="searchFieldSelect">
          <label htmlFor="filter">Search filter: </label>
          <select
            id="filter"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <option value="businessName">Customer Name</option>
            <option value="productName">Product Name</option>
            <option value="truckNumber">Truck Number</option>
            <option value="combined">Mixed search</option>
          </select>
        </div>
        {searchField === "combined" ? (
          <div className="combinedSearch">
            <label htmlFor="businessQuery" className="sr-only">Customer Name</label>
            <input
              id="businessQuery"
              type="text"
              placeholder="Customer Name"
              value={businessQuery}
              onChange={(e) => setBusinessQuery(e.target.value)}
            />
            <label htmlFor="productQuery" className="sr-only">Product Name</label>
            <input
              id="productQuery"
              type="text"
              placeholder="Product Name"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
            />
            <label htmlFor="truckQuery" className="sr-only">Truck Number</label>
            <input
              id="truckQuery"
              type="text"
              placeholder="Truck Number"
              value={truckQuery}
              onChange={(e) => setTruckQuery(e.target.value)}
            />
          </div>
        ) : (
          <div className="apInput">
            <label htmlFor="searchQuery" className="sr-only">Search</label>
            <input
              id="searchQuery"
              type="text"
              value={searchQuery}
              placeholder="Search"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="apIcon" />
          </div>
        )}
        <div className="filterBy">
          Filter By:
          <label htmlFor="fromDate" className="sr-only">From Date</label>
          <input
            id="fromDate"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <label htmlFor="toDate" className="sr-only">To Date</label>
          <input
            id="toDate"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <div className="searchFieldSelect">
            <label htmlFor="statusFilter">Filter by Status: </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="utilized">Utilized</option>
            </select>
          </div>
        </div>
      </div>
      <div className="invoiceTable">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2>Please wait while we process the request...</h2>
          </div>
        ) : error ? (
          <div className="m-auto text-center px-4 py-4">
            <h1>Error</h1>
            <p>{error}</p>
          </div>
        ) : paginatedAllocations.length > 0 ? (
          <>
            <div className="table-wrapper">
              <table className="inTable">
                <thead>
                  <tr className="heTable">
                    <th>S/N</th>
                    <th>License No</th>
                    <th>Customer</th>
                    <th>License Owner</th>
                    <th>Destination</th>
                    <th>Waybill</th>
                    <th>Truck No</th>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAllocations.map((allocation, index) => (
                    <tr key={allocation._id || index} className="taData">
                      <td>{page * rowsPerPage + index + 1}</td>
                      <td>{allocation.license?.licenseNumber || "N/A"}</td>
                      <td>{allocation.customer?.businessName || "N/A"}</td>
                      <td>{allocation.license?.licenseOwner || "N/A"}</td>
                      <td>{allocation.destination || "N/A"}</td>
                      <td>{allocation.invoice || "N/A"}</td>
                      <td>{allocation.truckNumber || "N/A"}</td>
                      <td>{allocation.product?.name || allocation.itemNumber || "N/A"}</td>
                      <td>{allocation.quantity || 0}</td>
                      <td>{allocation.exDepotPrice || 0}</td>
                      <td>{allocation.status || "N/A"}</td>
                      <td>
                        {new Date(allocation.updatedAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="m-auto text-center px-4 py-4">
            <h1>No data available.</h1>
            <p>Please check back later or adjust your filters.</p>
          </div>
        )}
      </div>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredAllocations.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <div className="dowBtn">
        <button onClick={generatePDF}>Print Report</button>
        <button onClick={exportToExcel}>Export</button>
        <button onClick={exportToExcelExternal}>Export external</button>
      </div>
    </div>
  );
};

export default AllocationReport;